import { gql, useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import FormLayout from 'features/App/components/FormLayout'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { ReceiveBulkItems } from 'features/Inventories/components/ReceiveBulkItems'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { ORDER_TYPE } from 'features/Inventories/components/OrderType/constants'
import { TYPE } from 'features/Inventories/components/Type/constants'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { sanitizePrice, round } from 'utils/math'
import { canWriteBase, canWriteWarehouse, canReadInventory } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  helmet: {
    id: 'inventories.receiveBulkItems.helmet',
    defaultMessage: 'Receive items | Nexbus Admin'
  }
})

const CREATE_ORDER = gql`
  mutation CreateOrder($input: CreateOrderInput!) {
    createOrder(input: $input)
  }
`

const GET_INVENTORY = gql`
  query GetInventory($inventoryId: ID!) {
    inventory(input: { id: $inventoryId }) {
      id
      company {
        id
        name
      }
      name
      type
    }
  }
`

const ReceiveBulkItemsView = (): TSFixMe => {
  const navigate = useNavigate()
  const { inventoryId } = useParams()
  const intl = useIntl()
  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_INVENTORY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !canReadInventory(login.accessToken.policies),
    variables: {
      inventoryId: inventoryId
    }
  })
  const inventory = (queryData && queryData.inventory) || {}

  let content

  const [createOrder, { loading: mutationLoading, error: mutationError }] = useMutation(CREATE_ORDER, {
    onCompleted() {
      navigate(`/inventories/${inventoryId}/inbounds`)
    }
  })

  const handleSubmit = (values: TSFixMe): TSFixMe => {
    const items = values.items.reduce((result: TSFixMe, item: TSFixMe): TSFixMe => {
      return [
        ...result,
        ...item.nestedItems.map((nestedItem: TSFixMe): TSFixMe => {
          const packagesQuantity = item.isPackage ? item.packagesQuantity : 1

          return {
            id: nestedItem.id,
            quantity: round(nestedItem.quantity * packagesQuantity),
            cost: sanitizePrice(nestedItem.price * 100, true)
          }
        })
      ]
    }, [])

    createOrder({
      variables: {
        input: {
          type: ORDER_TYPE.PURCHASE,
          destinationID: inventoryId,
          purchaseID: values.purchaseId,
          supplier: values.supplier,
          lineItems: items
        }
      }
    })
  }

  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (
    !canReadInventory(login.accessToken.policies) ||
    (!queryLoading && inventory.type === TYPE.BASE && !canWriteBase(login.accessToken.policies)) ||
    (!queryLoading && inventory.type === TYPE.WAREHOUSE && !canWriteWarehouse(login.accessToken.policies))
  ) {
    return <ForbiddenView feature={FEATURES.INVENTORY} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="spanish.inventories.receiveBulkItems.error.description"
              defaultMessage="Hubo un error al cargar tu ubicación"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage
                id="spanish.inventories.receiveBulkItems.error.action"
                defaultMessage="Recarga tu ubicación"
              />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <ReceiveBulkItems onSubmit={handleSubmit} loading={mutationLoading} error={mutationError} inventory={inventory} />
    )
  }

  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage
            id="spanish.inventories.receiveBulkItems.title"
            defaultMessage="Entrada para la ubicación {inventoryName}"
            values={{
              inventoryName: inventory.name
            }}
          />
        </Heading.H3>
      </FormLayout.Header>

      <FormLayout.Content>
        <Card.Container>{content}</Card.Container>
      </FormLayout.Content>
    </div>
  )
}

export default withLastUrl(ReceiveBulkItemsView)
